import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Lock, Mail, RefreshCw } from "lucide-react";
import { api, API } from "@/lib/api";

const TOKEN_KEY = "bayo_admin_token";

export default function AdminLeadsPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = async (t) => {
    setError("");
    try {
      const [s, l] = await Promise.all([api.adminStats(t), api.adminLeads(t)]);
      setStats(s); setData(l);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, t);
    } catch {
      setError("Token invalide");
      setAuthed(false);
    }
  };

  useEffect(() => { if (token) load(token); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(""); setAuthed(false); setData(null); setStats(null); };

  const downloadCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads.csv`;
    fetch(url, { headers: { "X-Admin-Token": token } }).then(async (r) => {
      const blob = await r.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "bayo-leads.csv";
      link.click();
    });
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-6 pt-32">
        <Lock size={28} className="mb-6 text-[color:var(--ink-2)]" />
        <h1 className="font-serif-display text-4xl mb-3">Admin BAYO</h1>
        <p className="text-[color:var(--ink-2)] mb-10">Entrez le token administrateur pour accéder à la liste des leads.</p>
        <form onSubmit={(e) => { e.preventDefault(); load(token); }} className="space-y-6">
          <div>
            <label className="label-line">Admin token</label>
            <input
              data-testid="admin-token"
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              className="input-line"
              placeholder="••••••••••"
            />
          </div>
          {error && <p className="text-sm text-[color:var(--red)]">{error}</p>}
          <button data-testid="admin-submit" className="btn-navy">Se connecter</button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <p className="eyebrow mb-2">Tableau de bord</p>
          <h1 className="font-serif-display text-5xl">Leads & stats</h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Link to="/admin/recipes" className="btn-outline" data-testid="admin-link-recipes">Modération recettes</Link>
          <button onClick={() => load(token)} className="btn-outline" data-testid="admin-refresh"><RefreshCw size={14} /> Refresh</button>
          <button onClick={downloadCsv} className="btn-navy" data-testid="admin-export-csv"><Download size={14} /> Exporter CSV</button>
          <button onClick={logout} className="text-xs uppercase tracking-[0.22em] underline">Déconnexion</button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {[
            { k: "leads", label: "Leads totaux", v: stats.leads },
            { k: "ebook_leads", label: "Leads ebook", v: stats.ebook_leads, accent: true },
            { k: "users", label: "Utilisateurs", v: stats.users },
            { k: "orders", label: "Commandes", v: stats.orders },
            { k: "paid_orders", label: "Commandes payées", v: stats.paid_orders, accent: true },
            { k: "recipes", label: "Recettes", v: stats.recipes },
            { k: "reviews", label: "Avis", v: stats.reviews },
          ].map((c) => (
            <div key={c.k} data-testid={`admin-stat-${c.k}`} className={`border ${c.accent ? "border-[color:var(--red)]" : "border-[color:rgba(0,16,48,0.15)]"} p-6`}>
              <p className="eyebrow mb-2">{c.label}</p>
              <p className="font-serif-display text-4xl">{c.v}</p>
            </div>
          ))}
        </div>
      )}

      {/* Source breakdown */}
      {data && (
        <>
          <h2 className="font-serif-display text-3xl mb-2">Sources</h2>
          <div className="flex flex-wrap gap-3 mb-10">
            {Object.entries(data.by_source).map(([k, v]) => (
              <span key={k} className="country-chip">{k} · {v}</span>
            ))}
          </div>

          <h2 className="font-serif-display text-3xl mb-6">Tous les leads ({data.total})</h2>
          <div className="border border-[color:rgba(0,16,48,0.12)]">
            <table className="w-full text-sm" data-testid="admin-leads-table">
              <thead className="bg-[var(--bg-2)] text-[color:var(--ink-2)]">
                <tr>
                  <th className="text-left p-3 uppercase tracking-[0.18em] text-xs">Email</th>
                  <th className="text-left p-3 uppercase tracking-[0.18em] text-xs">Source</th>
                  <th className="text-left p-3 uppercase tracking-[0.18em] text-xs">Lang</th>
                  <th className="text-left p-3 uppercase tracking-[0.18em] text-xs">Inscrit le</th>
                  <th className="text-left p-3 uppercase tracking-[0.18em] text-xs">DL</th>
                </tr>
              </thead>
              <tbody>
                {data.leads.map((l) => (
                  <tr key={l.email} className="border-t border-[color:rgba(0,16,48,0.08)]">
                    <td className="p-3 inline-flex items-center gap-2"><Mail size={12} />{l.email}</td>
                    <td className="p-3"><span className="country-chip text-[10px]">{l.source || "—"}</span></td>
                    <td className="p-3 text-[color:var(--ink-3)]">{l.ebook_lang || "—"}</td>
                    <td className="p-3 text-[color:var(--ink-3)]">{l.created_at?.slice(0, 10)}</td>
                    <td className="p-3 text-[color:var(--ink-3)]">{l.download_count || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
